import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import api from '../../lib/api';

const ForgotPasswordModal = ({ isOpen, onClose, onBackToLogin }) => {
  const [step, setStep] = useState(1); // 1 = email, 2 = code + new password
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSendCode = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await api.post('/auth/forgot-password', { email });
      toast.success('Код восстановления отправлен на почту');
      setStep(2);
    } catch (error) {
      toast.error('Ошибка отправки');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      toast.error('Пароли не совпадают');
      return;
    }

    if (newPassword.length < 6) {
      toast.error('Пароль должен содержать минимум 6 символов');
      return;
    }

    setLoading(true);

    try {
      await api.post('/auth/reset-password', {
        email,
        code,
        new_password: newPassword
      });
      toast.success('Пароль успешно изменён!');
      handleClose();
      onBackToLogin();
    } catch (error) {
      const errorMessage = error.response?.data?.detail;
      toast.error(errorMessage || 'Неверный код или ошибка сброса пароля');
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    setLoading(true);
    try {
      await api.post('/auth/forgot-password', { email });
      toast.success('Новый код отправлен на почту');
    } catch (error) {
      toast.error('Ошибка отправки кода');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setStep(1);
    setEmail('');
    setCode('');
    setNewPassword('');
    setConfirmPassword('');
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md" data-testid="forgot-password-modal">
        <DialogHeader>
          <DialogTitle className="text-2xl font-semibold text-center" style={{ color: '#1F1F1F' }}>
            {step === 1 ? 'Восстановление пароля' : 'Новый пароль'}
          </DialogTitle>
          <p className="text-center text-[#7A7A7A] mt-2">
            {step === 1 
              ? 'Введите ваш email для получения\nкода восстановления'
              : `Введите код и новый пароль`
            }
          </p>
        </DialogHeader>

        {step === 1 ? (
          <form onSubmit={handleSendCode} className="space-y-4 mt-4">
            <div>
              <Label htmlFor="reset-email" className="text-[#1F1F1F]">Электронная почта</Label>
              <Input
                id="reset-email"
                type="email"
                placeholder="email@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="mt-1"
                data-testid="forgot-password-email-input"
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full py-6 rounded-full text-white font-semibold text-lg"
              style={{ background: 'linear-gradient(135deg, #1A73E8 0%, #6A9EFF 100%)' }}
              data-testid="forgot-password-submit-button"
            >
              {loading ? 'Отправка...' : 'ПОЛУЧИТЬ КОД'}
            </Button>

            <div className="text-center">
              <button
                type="button"
                onClick={() => {
                  handleClose();
                  onBackToLogin();
                }}
                className="text-[#1A73E8] text-sm hover:underline"
                data-testid="back-to-login-button"
              >
                Вернуться ко входу
              </button>
            </div>
          </form>
        ) : (
          <form onSubmit={handleResetPassword} className="space-y-4 mt-4">
            <div>
              <Label htmlFor="reset-code" className="text-[#1F1F1F]">Код восстановления</Label>
              <Input
                id="reset-code"
                type="text"
                placeholder="000000"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                required
                className="mt-1 text-center text-2xl tracking-widest"
                maxLength={6}
                data-testid="reset-code-input"
              />
              <p className="text-xs text-[#7A7A7A] mt-1 text-center">
                Код отправлен на {email}
              </p>
            </div>

            <div>
              <Label htmlFor="new-password" className="text-[#1F1F1F]">Новый пароль</Label>
              <Input
                id="new-password"
                type="password"
                placeholder="Минимум 6 символов"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                minLength={6}
                className="mt-1"
                data-testid="new-password-input"
              />
            </div>

            <div>
              <Label htmlFor="confirm-password" className="text-[#1F1F1F]">Подтвердите пароль</Label>
              <Input
                id="confirm-password"
                type="password"
                placeholder="Повторите пароль"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                minLength={6}
                className="mt-1"
                data-testid="confirm-password-input"
              />
            </div>

            <Button
              type="submit"
              disabled={loading || code.length !== 6}
              className="w-full py-6 rounded-full text-white font-semibold text-lg"
              style={{ background: 'linear-gradient(135deg, #34C759 0%, #5DD97C 100%)' }}
              data-testid="reset-password-button"
            >
              {loading ? 'Сохранение...' : 'СОХРАНИТЬ ПАРОЛЬ'}
            </Button>

            <div className="text-center">
              <button
                type="button"
                onClick={handleResendCode}
                disabled={loading}
                className="text-[#1A73E8] hover:underline text-sm"
                data-testid="resend-reset-code-button"
              >
                Отправить код повторно
              </button>
            </div>

            <div className="text-center">
              <button
                type="button"
                onClick={() => setStep(1)}
                className="text-[#7A7A7A] hover:text-[#1F1F1F] text-sm"
              >
                ← Изменить email
              </button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ForgotPasswordModal;
